import React, { Fragment, useContext, useEffect, useMemo, useState, useRef, useCallback } from 'react';
import { Link, useLocation, useMatch, useNavigate, useParams } from 'react-router-dom';
import DownloadFileModal from '../../components/common/download-file-modal';
import { GlobalContext, handleError } from '../../context/Provider';
import henceforthApi from '../../utils/henceforthApi';
import moment from 'moment';
import BreadCrumb from '../../components/common/BreadCrumb';
import { numberWithCommas } from '../../utils/validations';
import { toast } from 'react-toastify';
import AgTable from '../../components/common/AgTable';
import { ColDef } from 'ag-grid-community';
import defaultImage from '../../assets/images/pages/defaultImage.jpg';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import {
  Search,
  X,
  Download,
  Copy,
  Check,
  DollarSign,
  Calendar as CalendarIcon,
  RotateCcw,
  ChevronDown,
  Eye,
  TrendingUp,
  CreditCard,
  Receipt,
  Wallet,
  ShoppingBag,
  Clock,
} from 'lucide-react';
import henceofrthEnums from '../../utils/henceofrthEnums';
import SelectDropdown, { SelectDropdownOption } from '../../components/ui/select-dropdown';
import { getStatusBadge } from '../../utils/orderStatusBadge';
import { Button } from '../../components/ui/button';

const AllEarnings: React.FC = () => {
  const limit = 10;
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const matchEarnings = useMatch('/earnings/:page');
  const matchTransactions = useMatch('/orders/transactions/:page');
  const currentPageParam = matchEarnings?.params?.page || matchTransactions?.params?.page || params?.page || '1';

  const { language_data, authState, authDispatch, handleSearch, onFilterPriceHandler } =
    useContext(GlobalContext);

  const breadCrumbPath = [
    {
      name: `${language_data?.[authState.lang]?.common_home || 'Home'}`,
      url: `/`,
      active: '',
    },
    {
      name: `${language_data?.[authState.lang]?.common_earning || 'Earnings'}`,
      url: ``,
      active: 'not-allowed',
    },
  ];

  const newParam = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const [transactionList, setTransactionList] = useState<{ total_count: number; data: any[] }>({
    total_count: 0,
    data: [],
  });
  const [loading, setLoading] = useState(false);

  // Local Search state
  const [searchTerm, setSearchTerm] = useState(newParam.get('search') || '');
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Price popover state
  const [showPricePopover, setShowPricePopover] = useState(false);
  const [minPriceInput, setMinPriceInput] = useState(newParam.get('min_price') || '');
  const [maxPriceInput, setMaxPriceInput] = useState(newParam.get('max_price') || '');
  const pricePopoverRef = useRef<HTMLDivElement>(null);

  // Date popover state
  const [showDatePopover, setShowDatePopover] = useState(false);
  const datePopoverRef = useRef<HTMLDivElement>(null);

  // Sync inputs with URL params
  useEffect(() => {
    setSearchTerm(newParam.get('search') || '');
    setMinPriceInput(newParam.get('min_price') || '');
    setMaxPriceInput(newParam.get('max_price') || '');
  }, [newParam]);

  // Click outside listeners
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (pricePopoverRef.current && !pricePopoverRef.current.contains(e.target as Node)) {
        setShowPricePopover(false);
      }
      if (datePopoverRef.current && !datePopoverRef.current.contains(e.target as Node)) {
        setShowDatePopover(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const transactionListing = async () => {
    setLoading(true);
    try {
      const pageNum = Number(currentPageParam) || 1;
      const apiRes = (
        await henceforthApi.Transaction.getTransactions(pageNum - 1, limit, newParam.toString())
      ).data;
      setTransactionList({
        total_count: apiRes?.total_count || 0,
        data: apiRes?.data || [],
      });
    } catch (err: any) {
      console.log('err', err?.response?.body?.error_description);
      setTransactionList({
        total_count: 0,
        data: [],
      });
      handleError(err, '', authDispatch);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    transactionListing();
  }, [
    currentPageParam,
    newParam.get('search'),
    newParam.get('order_status'),
    newParam.get('payment_status'),
    newParam.get('product_id'),
    newParam.get('min_price'),
    newParam.get('max_price'),
    newParam.get('start_date'),
    newParam.get('end_date'),
  ]);

  const copyText = useCallback(
    (id: string, name: string) => {
      if (id) {
        navigator?.clipboard?.writeText(id);
        setCopiedId(id);
        toast.success(
          `${name} ${language_data?.[authState.lang]?.common_copy || 'copied'} ${language_data?.[authState.lang]?.common_successfull || 'successfully'
          }`
        );
        setTimeout(() => {
          setCopiedId((curr) => (curr === id ? null : curr));
        }, 2000);
      }
    },
    [authState, language_data]
  );

  const exportData = useCallback(
    async (startDate: number, endDate: number) => {
      try {
        const apiRes = await henceforthApi.Transaction.export(startDate, endDate);
        const data = apiRes.data.data;
        const rows = [
          [
            'Sr.',
            'Order ID',
            'Transactions ID',
            'Product ID',
            'Product Name',
            'Customer Name',
            'Date Time',
            'Order Status',
            'Order Price',
            'Earning',
          ],
        ];
        if (Array.isArray(data)) {
          data.forEach((res: any, index: number) => {
            rows.push([
              String(index + 1),
              res?.order_id || 'N/A',
              res?.pm_id || 'N/A',
              res?.product_id?.prodct_id || 'N/A',
              res?.product_id?.name || 'N/A',
              res?.user_id?.name || 'N/A',
              res?.created_at ? moment(Number(res?.created_at)).format('DD MMM YYYY hh:mm:ss') : '',
              res?.order_status || res?.payment_status || 'N/A',
              String(res?.total_price || 0),
              String(res?.total_earnings || 0),
            ]);
          });
        }
        const csvContent =
          'data:text/csv;charset=utf-8,' +
          rows.map((e) => e.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(',')).join('\n');
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement('a');
        link.setAttribute('href', encodedUri);
        link.setAttribute('download', `earnings_transactions_${moment().valueOf()}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        const closeModal = document.getElementById('closeModal');
        if (closeModal) {
          closeModal.click();
        }
      } catch (err: any) {
        console.log(err?.response?.body?.error_description);
        handleError(err, 'active', authDispatch);
      }
    },
    [authDispatch]
  );

  // Search handler
  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleSearch('search', searchTerm.trim());
  };

  const handleClearSearch = () => {
    setSearchTerm('');
    handleSearch('search', '');
  };

  // Status handler
  const handleStatusFilter = (val: string) => {
    handleSearch('order_status', val);
  };

  // Price handler
  const handleApplyPrice = (e: React.FormEvent) => {
    e.preventDefault();
    if (minPriceInput && maxPriceInput && Number(minPriceInput) > Number(maxPriceInput)) {
      toast.warn(
        `${language_data?.[authState.lang]?.common_max || 'Max'} price should be greater than ${language_data?.[authState.lang]?.common_min || 'Min'
        } price`
      );
      return;
    }
    onFilterPriceHandler(minPriceInput, maxPriceInput);
    setShowPricePopover(false);
  };

  const handleClearPrice = () => {
    setMinPriceInput('');
    setMaxPriceInput('');
    onFilterPriceHandler('', '');
    setShowPricePopover(false);
  };

  // Date handler
  const handleDateSelect = (val: any) => {
    if (Array.isArray(val) && val[0] && val[1]) {
      handleSearch('start_date', String(moment(val[0]).startOf('day').valueOf()));
      handleSearch('end_date', String(moment(val[1]).endOf('day').valueOf()));
      setShowDatePopover(false);
    }
  };

  const handleClearDate = () => {
    handleSearch('start_date', '');
    setShowDatePopover(false);
  };

  // Reset all filters
  const handleResetAllFilters = () => {
    setSearchTerm('');
    setMinPriceInput('');
    setMaxPriceInput('');
    navigate({
      pathname: '/earnings/1',
      search: '',
    });
  };

  const currentStatus = newParam.get('order_status') || newParam.get('payment_status') || '';
  const hasSearch = Boolean(newParam.get('search'));
  const hasStatus = Boolean(currentStatus);
  const hasPrice = Boolean(newParam.get('min_price') || newParam.get('max_price'));
  const hasDate = Boolean(newParam.get('start_date'));
  const isAnyFilterActive = hasSearch || hasStatus || hasPrice || hasDate;

  // Earnings KPI calculations
  const summaryKpis = useMemo(() => {
    const data = transactionList.data || [];
    const totalEarnings = data.reduce(
      (sum, item) => sum + (Number(item?.total_earnings) || 0),
      0
    );
    const totalGross = data.reduce((sum, item) => sum + (Number(item?.total_price) || 0), 0);
    const avgEarnings = data.length > 0 ? totalEarnings / data.length : 0;
    return {
      totalEarnings,
      totalGross,
      avgEarnings,
      totalTransactions: transactionList.total_count || 0,
    };
  }, [transactionList]);

  // AG Table Columns
  const columnDefs: ColDef[] = useMemo(() => {
    const pageNum = Number(currentPageParam) || 1;
    return [
      {
        headerName: '#',
        field: '_index',
        width: 65,
        minWidth: 55,
        sortable: false,
        filter: false,
        pinned: 'left',
        cellRenderer: (params: any) => {
          const index = (pageNum - 1) * limit + (params.node?.rowIndex ?? 0) + 1;
          return (
            <div className="flex items-center h-full text-slate-400 font-bold text-xs">
              {index}
            </div>
          );
        },
      },
      {
        headerName: `${language_data?.[authState.lang]?.common_order || 'Order'} ${language_data?.[authState.lang]?.common_id || 'ID'
          }`,
        field: 'order_id',
        width: 155,
        minWidth: 140,
        pinned: 'left',
        cellRenderer: (params: any) => {
          const orderId = params.data?.order_id || '—';
          const isCopied = copiedId === orderId && orderId !== '—';
          return (
            <div className="flex items-center gap-1.5 h-full">
              <span className="font-mono text-xs font-black text-slate-800 bg-slate-100 px-2.5 py-1 rounded-lg border border-slate-200/80 truncate max-w-[105px]">
                {orderId}
              </span>
              {orderId !== '—' && (
                <button
                  type="button"
                  onClick={() => copyText(orderId, 'Order ID')}
                  className="p-1 text-slate-400 hover:text-slate-700 rounded transition-colors cursor-pointer"
                  title="Copy Order ID"
                >
                  {isCopied ? (
                    <Check className="w-3.5 h-3.5 text-emerald-600" />
                  ) : (
                    <Copy className="w-3.5 h-3.5" />
                  )}
                </button>
              )}
            </div>
          );
        },
      },
      {
        headerName: `${language_data?.[authState.lang]?.common_product || 'Product'}`,
        field: 'product_id.name',
        minWidth: 240,
        cellRenderer: (params: any) => {
          const product = params.data?.product_id;
          const pic = product?.images?.[0]
            ? `${henceforthApi.API_FILE_ROOT_SMALL}${product.images[0]}`
            : defaultImage;
          const category = product?.category_id?.name || '';
          const title = product?.name || 'Product Item';
          return (
            <div className="flex items-center gap-3 h-full py-1">
              <img
                src={pic}
                alt={title}
                className="w-9 h-9 rounded-lg object-cover border border-slate-200 shrink-0"
              />
              <div className="flex flex-col justify-center overflow-hidden">
                {category && (
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider truncate">
                    {category}
                  </span>
                )}
                <span className="text-xs font-semibold text-slate-800 truncate" title={title}>
                  {title}
                </span>
              </div>
            </div>
          );
        },
      },
      {
        headerName: `${language_data?.[authState.lang]?.common_customer || 'Customer'}`,
        field: 'user_id.name',
        minWidth: 190,
        cellRenderer: (params: any) => {
          const user = params.data?.user_id;
          const pic = user?.profile_pic
            ? `${henceforthApi.API_FILE_ROOT_SMALL}${user.profile_pic}`
            : defaultImage;
          const name = user?.name || language_data?.[authState.lang]?.common_not_available || 'Customer';
          return (
            <div className="flex items-center gap-2.5 h-full py-1">
              <img
                src={pic}
                alt={name}
                className="w-8 h-8 rounded-full object-cover border border-slate-200 shrink-0"
              />
              <span className="font-semibold text-slate-800 text-xs sm:text-sm truncate">
                {name}
              </span>
            </div>
          );
        },
      },
      {
        headerName: `${language_data?.[authState.lang]?.common_date || 'Date'} & ${language_data?.[authState.lang]?.common_time || 'Time'
          }`,
        field: 'created_at',
        minWidth: 170,
        cellRenderer: (params: any) => {
          const time = params.data?.created_at;
          if (!time) return <span className="text-slate-400 text-xs">—</span>;
          const formatted = moment(Number(time)).format('DD MMM YYYY, hh:mm A');
          return (
            <div className="flex items-center gap-1.5 h-full text-xs font-medium text-slate-600">
              <Clock className="w-3.5 h-3.5 text-slate-400" />
              <span>{formatted}</span>
            </div>
          );
        },
      },
      {
        headerName: `${language_data?.[authState.lang]?.common_status || 'Status'}`,
        field: 'payment_status',
        minWidth: 150,
        cellRenderer: (params: any) => {
          const statusVal = params.data?.payment_status || params.data?.order_status || 'CONFIRMED';
          const badge = getStatusBadge(statusVal);
          return (
            <div className="flex items-center h-full">
              <span
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold border ${badge.pill}`}
              >
                <span className={`w-1.5 h-1.5 rounded-full ${badge.dot}`} />
                {badge.label}
              </span>
            </div>
          );
        },
      },
      {
        headerName: `${language_data?.[authState.lang]?.common_order || 'Order'} ${language_data?.[authState.lang]?.common_amount || 'Total'
          }`,
        field: 'total_price',
        width: 125,
        minWidth: 110,
        cellRenderer: (params: any) => (
          <div className="flex items-center h-full font-bold text-slate-700 text-xs sm:text-sm">
            ${numberWithCommas(params.data?.total_price || 0)}
          </div>
        ),
      },
      {
        headerName: `${language_data?.[authState.lang]?.common_earning || 'Seller Earning'}`,
        field: 'total_earnings',
        width: 145,
        minWidth: 130,
        cellRenderer: (params: any) => (
          <div className="flex items-center h-full">
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-emerald-50 text-emerald-700 border border-emerald-200/80 font-black text-xs sm:text-sm">
              +${numberWithCommas(params.data?.total_earnings || 0)}
            </span>
          </div>
        ),
      },
      {
        headerName: `${language_data?.[authState.lang]?.common_action || 'Action'}`,
        field: '_id',
        width: 115,
        minWidth: 100,
        sortable: false,
        filter: false,
        pinned: 'right',
        cellRenderer: (params: any) => {
          const orderId = params.data?._id;
          const isBlocked =
            authState?.is_admin_verified === 'pending' || authState?.is_admin_verified === 'reject';
          return (
            <div className="flex items-center gap-1.5 h-full">
              <Button variant="default" size="sm" asChild>
                <Link
                  to={isBlocked ? '#' : `/order/${orderId}?transaction=true`}
                  onClick={(e) => {
                    if (authState?.is_admin_verified === 'pending') {
                      e.preventDefault();
                      toast.error('Your account is pending for admin verification.');
                    } else if (authState?.is_admin_verified === 'reject') {
                      e.preventDefault();
                      toast.error('Your account is rejected for admin verification.');
                    }
                  }}
                  title="View Transaction Order Details"
                >
                  <Eye className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">
                    {language_data?.[authState.lang]?.common_view || 'View'}
                  </span>
                </Link>
              </Button>
            </div>
          );
        },
      },
    ];
  }, [currentPageParam, limit, language_data, authState, copiedId, copyText]);

  return (
    <Fragment>
      {/* Standard Breadcrumb Bar */}
      <BreadCrumb pathNameDeclare={breadCrumbPath} />

      <div className="w-full px-4 sm:px-6 lg:px-8 py-4 space-y-4">
        {/* 1. Header Card with Title, Counter Badge & Export Button */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 bg-white px-5 py-4 rounded-2xl border border-slate-200/80 shadow-xs">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-xl sm:text-2xl font-black text-slate-900 tracking-tight m-0">
                {language_data?.[authState.lang]?.common_earning || 'Earnings'} & Transactions
              </h1>
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold bg-emerald-50 text-emerald-700 border border-emerald-200/60 shadow-2xs">
                <Wallet className="w-3.5 h-3.5 text-emerald-600 me-1" />
                {transactionList.total_count}{' '}
                {transactionList.total_count === 1 ? 'Transaction' : 'Transactions'}
              </span>
            </div>
            <p className="text-xs sm:text-sm text-slate-500 font-medium mt-1 mb-0">
              Track customer payments, gross sales revenue, and net earnings credited to your seller account.
            </p>
          </div>

          <div className="flex items-center gap-3 flex-wrap">
            {/* Export CSV Button */}
            <button
              type="button"
              data-bs-toggle="modal"
              data-bs-target="#fileDownloadModal"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 font-bold text-xs sm:text-sm shadow-2xs transition-all active:scale-[0.98] cursor-pointer"
            >
              <Download className="w-4 h-4 text-slate-500" />
              <span>{language_data?.[authState.lang]?.common_export_file || 'Export CSV'}</span>
            </button>
          </div>
        </div>
        {/* 3. Modern Filter & Search Toolbar */}
        <div className="bg-white p-4 rounded-2xl border border-slate-200/80 shadow-xs space-y-3.5">
          <div className="flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-3 flex-wrap">
            {/* Search Input */}
            <form onSubmit={handleSearchSubmit} className="relative max-w-[360px] flex-1 min-w-[260px]">
              <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-1/2 -translate-y-1/2 pointer-events-none" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by order ID, product SKU, or customer..."
                className="w-full pl-10 pr-9 py-2 text-xs sm:text-sm font-medium bg-slate-50/80 border border-slate-200 rounded-xl text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#1AA7DE]/20 focus:border-[#1AA7DE] transition-all"
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={handleClearSearch}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 p-0.5 rounded cursor-pointer"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </form>

            <div className="flex items-center gap-2 flex-wrap">
              {/* Status Dropdown Filter */}
              {(() => {
                const statusOptions: SelectDropdownOption[] = [
                  { value: '', label: 'All Statuses', subLabel: 'Show all orders' },
                  ...Object.values(henceofrthEnums.OrderStatus).map((s) => ({
                    value: s,
                    label: s.replace(/_/g, ' '),
                  })),
                ];

                return (
                  <SelectDropdown
                    heading="Order / Payment Status"
                    options={statusOptions}
                    value={currentStatus}
                    onChange={(val) => handleStatusFilter(val)}
                  />
                );
              })()}

              {/* Date Filter Popover */}
              <div className="relative" ref={datePopoverRef}>
                <button
                  type="button"
                  onClick={() => setShowDatePopover(!showDatePopover)}
                  className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-xl border text-xs sm:text-sm font-bold transition-all cursor-pointer ${hasDate
                      ? 'bg-sky-50 border-[#1AA7DE] text-[#1AA7DE]'
                      : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50 shadow-2xs'
                    }`}
                >
                  <CalendarIcon className="w-4 h-4 text-slate-500" />
                  <span>
                    {hasDate
                      ? `${moment(Number(newParam.get('start_date'))).format('DD MMM')} - ${moment(
                        Number(newParam.get('end_date'))
                      ).format('DD MMM')}`
                      : 'Date Range'}
                  </span>
                  <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
                </button>

                {showDatePopover && (
                  <div className="absolute right-0 top-full mt-2 bg-white rounded-2xl border border-slate-200 shadow-xl p-4 z-50 animate-in fade-in zoom-in-95">
                    <div className="flex items-center justify-between pb-2.5 border-b border-slate-100 mb-3">
                      <span className="text-xs font-bold uppercase tracking-wider text-slate-700">
                        Filter by Date
                      </span>
                      {hasDate && (
                        <button
                          type="button"
                          onClick={handleClearDate}
                          className="text-xs text-[#1AA7DE] font-semibold hover:underline cursor-pointer"
                        >
                          Clear
                        </button>
                      )}
                    </div>

                    <Calendar
                      selectRange={true}
                      onChange={handleDateSelect}
                      className="border-0 rounded-xl shadow-none"
                    />
                  </div>
                )}
              </div>

              {/* Price / Earning Filter Popover */}
              <div className="relative" ref={pricePopoverRef}>
                <button
                  type="button"
                  onClick={() => setShowPricePopover(!showPricePopover)}
                  className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-xl border text-xs sm:text-sm font-bold transition-all cursor-pointer ${hasPrice
                      ? 'bg-sky-50 border-[#1AA7DE] text-[#1AA7DE]'
                      : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50 shadow-2xs'
                    }`}
                >
                  <DollarSign className="w-4 h-4 text-slate-500" />
                  <span>
                    {hasPrice
                      ? `$${newParam.get('min_price') || '0'} - $${newParam.get('max_price') || '∞'}`
                      : 'Price Range'}
                  </span>
                  <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
                </button>

                {showPricePopover && (
                  <div className="absolute right-0 top-full mt-2 w-72 bg-white rounded-2xl border border-slate-200 shadow-xl p-4 z-50 animate-in fade-in zoom-in-95">
                    <div className="flex items-center justify-between pb-2.5 border-b border-slate-100 mb-3">
                      <span className="text-xs font-bold uppercase tracking-wider text-slate-700">
                        Filter by Amount
                      </span>
                      {hasPrice && (
                        <button
                          type="button"
                          onClick={handleClearPrice}
                          className="text-xs text-[#1AA7DE] font-semibold hover:underline cursor-pointer"
                        >
                          Clear
                        </button>
                      )}
                    </div>

                    <form onSubmit={handleApplyPrice} className="space-y-3">
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="text-[11px] font-bold text-slate-500 block mb-1">
                            Min ($)
                          </label>
                          <input
                            type="number"
                            value={minPriceInput}
                            onChange={(e) => setMinPriceInput(e.target.value)}
                            placeholder="0"
                            className="w-full px-3 py-1.5 text-xs border border-slate-200 rounded-lg text-slate-800 focus:outline-none focus:ring-1 focus:ring-[#1AA7DE]"
                          />
                        </div>
                        <div>
                          <label className="text-[11px] font-bold text-slate-500 block mb-1">
                            Max ($)
                          </label>
                          <input
                            type="number"
                            value={maxPriceInput}
                            onChange={(e) => setMaxPriceInput(e.target.value)}
                            placeholder="1000"
                            className="w-full px-3 py-1.5 text-xs border border-slate-200 rounded-lg text-slate-800 focus:outline-none focus:ring-1 focus:ring-[#1AA7DE]"
                          />
                        </div>
                      </div>

                      {/* Quick presets */}
                      <div className="flex flex-wrap gap-1.5 pt-1">
                        {[
                          { label: '$0 - $50', min: '0', max: '50' },
                          { label: '$50 - $200', min: '50', max: '200' },
                          { label: '$200 - $1000', min: '200', max: '1000' },
                        ].map((preset) => (
                          <button
                            key={preset.label}
                            type="button"
                            onClick={() => {
                              setMinPriceInput(preset.min);
                              setMaxPriceInput(preset.max);
                            }}
                            className="px-2 py-1 rounded text-[11px] font-semibold bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors cursor-pointer"
                          >
                            {preset.label}
                          </button>
                        ))}
                      </div>

                      <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
                        <button
                          type="submit"
                          className="flex-1 py-1.5 rounded-lg bg-[#1AA7DE] hover:bg-[#158ebf] text-white font-bold text-xs shadow-2xs transition-colors cursor-pointer"
                        >
                          Apply Filter
                        </button>
                        <button
                          type="button"
                          onClick={() => setShowPricePopover(false)}
                          className="px-3 py-1.5 rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50 font-semibold text-xs transition-colors cursor-pointer"
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  </div>
                )}
              </div>

              {/* Reset All Filters button */}
              {isAnyFilterActive && (
                <button
                  type="button"
                  onClick={handleResetAllFilters}
                  className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full border border-rose-200 text-xs sm:text-sm font-semibold text-rose-500 bg-white shadow-sm hover:shadow-md hover:border-rose-300 transition-all cursor-pointer"
                  title="Reset all filters"
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                  <span>Reset</span>
                </button>
              )}
            </div>
          </div>
        </div>

        {/* 4. AG Grid Modern Table */}
        <div className="bg-white rounded-2xl border border-slate-200/80 shadow-xs overflow-hidden">
          <AgTable
            rowData={transactionList?.data || []}
            columnDefs={columnDefs}
            loading={loading}
            pagination={true}
            page={Number(currentPageParam) || 1}
            limit={Number(limit)}
            count={transactionList?.total_count}
            emptyMessage={
              language_data?.[authState.lang]?.common_no_data_found || 'No Transactions Found'
            }
            emptySubtitle="Earnings records and financial transaction settlements will appear here as soon as orders are placed and processed."
          />
        </div>
      </div>

      {/* CSV Export Modal */}
      <DownloadFileModal exportData={exportData} />
    </Fragment>
  );
};

export default AllEarnings;